import { useState } from 'react';
import { CognitoIdentityProviderClient, RespondToAuthChallengeCommand } from '@aws-sdk/client-cognito-identity-provider';
import * as Toast from '@radix-ui/react-toast';

const client = new CognitoIdentityProviderClient({ region: 'us-east-1' });
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

const validatePassword = (password: string): string[] => {
  const errors: string[] = [];

  if (password.length < 8) errors.push('8-character minimum length');
  if (!/[0-9]/.test(password)) errors.push('At least 1 number');
  if (!/[a-z]/.test(password)) errors.push('At least 1 lowercase letter');
  if (!/[A-Z]/.test(password)) errors.push('At least 1 uppercase letter');
  if (!/[^\w\s]|(?<!^)\s(?!$)/.test(password)) {
    errors.push('At least 1 special character or internal space');
  }

  return errors;
};

export default function ChangePasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);


  const showToast = (message: string) => {
    setToastMessage(message);
    setToastOpen(false); // Reset to trigger again
    requestAnimationFrame(() => setToastOpen(true));
  };

  const handleSubmit = async () => {
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      return;
    }

    const session = localStorage.getItem('challengeSession');
    const username = localStorage.getItem('challengeUsername');

    if (!session || !username) {
      showToast('Missing session info');
      return;
    }

    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
          'userAttributes.preferred_username': username,
        },
      });

      const response = await client.send(command);
      if (response.AuthenticationResult?.AccessToken) {
        localStorage.removeItem('challengeSession');
        localStorage.removeItem('challengeUsername');
        showToast('Password changed successfully!');
        setTimeout(() => (window.location.href = '/'), 1500);
      }
    } catch (err) {
      console.error(err);
      showToast('Password change failed');
    }
  };

  return (
    <>
      <div style={{
        maxWidth: '400px',
        margin: '40px auto',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#f9f9f9',
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>
          Change Password
        </h2>

        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password"
          style={{
            width: '100%',
            padding: '12px 10px',
            fontSize: '16px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            marginBottom: '15px',
            boxSizing: 'border-box',
            backgroundColor: 'var(--muted)',
            color: 'var(--muted-foreground)',
            caretColor: 'var(--muted-foreground)',
          }}
          autoFocus
        />
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '15px',
            fontSize: '14px',
          }}
        >
          <strong style={{ display: 'block', marginBottom: '8px' }}>
            Requirements:
          </strong>
          {[
            {
              label: '8-character minimum length',
              test: (pw: string) => pw.length >= 8,
            },
            {
              label: 'Contains at least 1 number',
              test: (pw: string) => /\d/.test(pw),
            },
            {
              label: 'Contains at least 1 lowercase letter',
              test: (pw: string) => /[a-z]/.test(pw),
            },
            {
              label: 'Contains at least 1 uppercase letter',
              test: (pw: string) => /[A-Z]/.test(pw),
            },
            {
              label:
                'Contains at least 1 special character or internal space',
              test: (pw: string) =>
                /[^\w\s]/.test(pw) || (pw.includes(' ') && !/^\s|\s$/.test(pw)),
            },
          ].map((req, index) => {
            const passed = req.test(newPassword);
            return (
              <div
                key={index}
                style={{
                  color: passed ? '#16A34A' : '#DC2626', // green / red
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>{passed ? '✔️' : '❌'}</span> {req.label}
              </div>
            );
          })}
        </div>

        <button
          onClick={handleSubmit}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#4F46E5',  // Indigo-600
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
          }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#4338CA')}  // Indigo-700 hover
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4F46E5')}
          disabled={!newPassword.trim()}
        >
          Submit
        </button>
        <Toast.Provider swipeDirection="right">
          <Toast.Root open={toastOpen} onOpenChange={setToastOpen} duration={4000}>
            <Toast.Title className="text-sm font-medium px-4 py-2">
              {toastMessage}
            </Toast.Title>
          </Toast.Root>
          <Toast.Viewport
            className="absolute left-0 right-0 bottom-[-80px] mx-auto w-full max-w-md outline-none"
          />
        </Toast.Provider>
      </div>
    </>
  );
}
